import { useMemo, useState } from "react"

import { App, Avatar, Checkbox, Dropdown, Flex, Modal, Spin, Switch, Typography } from "antd"
import classname from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import Icon from "../../../../../Icon"
import DescriptorHeaderAddon from "../../../../ConfigurePage/Enrollments/components/DescriptorHeaderAddon"
import { descriptorStatusTypeMap } from "../../../../ConfigurePage/utils/Enrollment.util"
import {
  bulkUpdateAdminDescriptorStatus,
  updateAdminDescriptorStatus,
  updateAdminDescriptorType
} from "../../../../User/redux/user.thunks"
import useBulkDescriptors from "./useBulkDescriptors"

import "./BulkEditModal.less"

const { Text } = Typography

const STATUS_OPTIONS = [
  { key: "pending", label: "Pending" },
  { key: "pending_unenroll", label: "Pending unenroll" },
  { key: "in_re_enrollment", label: "In re-enrollment" },
  { key: "in_progress", label: "In progress" },
  { key: "active", label: "Active" },
  { key: "enrolled_elsewhere", label: "Enrolled elsewhere" },
  { key: "generic_descriptor", label: "Generic descriptor" },
  { key: "banned_descriptor", label: "Banned descriptor" },
  { key: "invalid_descriptor", label: "Invalid descriptor" },
  { key: "unenrolled", label: "Unenrolled" },
  { key: "verification_needed", label: "Verification needed" },
  { key: "invalid_arns", label: "Invalid ARN/BIN/CAID" },
  { key: "needs_assistance", label: "Needs assistance" }
]

const MATCH_OPTIONS = [
  { key: "Exact match", label: "Exact match" },
  { key: "Starts with", label: "Starts with" }
]

const getStatusLabel = (key) => STATUS_OPTIONS.find((item) => item.key === key)?.label || key

const getStatusType = (key) => descriptorStatusTypeMap[key] || "pending"

const BulkEditModalBody = ({ enrollment, businessAccountName, onClose }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { notification } = App.useApp()

  const enrollmentKey = enrollment?.name?.toLowerCase()
  const enrollmentLabel =
    enrollmentKey === "ethoca" ? "Ethoca" : (enrollmentKey || "").toUpperCase()
  const enrollmentIcon = enrollmentKey === "ethoca" ? "mastercard" : "visa"

  const { descriptors, loading, setDescriptors } = useBulkDescriptors(enrollment?.id, true)

  const [selectedIds, setSelectedIds] = useState([])
  const [bulkStatus, setBulkStatus] = useState(null)
  const [bulkMatch, setBulkMatch] = useState(null)
  const [sendEmail, setSendEmail] = useState(true)
  const [applying, setApplying] = useState(false)
  // Per-row status/match edits, staged locally until Apply - keyed by
  // billing_descriptor_id, independent of the checkbox/bulk flow below.
  const [rowOverrides, setRowOverrides] = useState({})

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const hasRowOverrides = Object.keys(rowOverrides).length > 0

  const allSelected = descriptors.length > 0 && selectedIds.length === descriptors.length
  const someSelected = selectedIds.length > 0 && !allSelected

  const applyEnabled =
    ((selectedIds.length > 0 && (bulkStatus || bulkMatch)) || hasRowOverrides) && !applying

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? descriptors.map((item) => item.billing_descriptor_id) : [])
  }

  const handleToggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  const patchDescriptor = (id, patch) => {
    // `id` here comes from an Object.entries(rowOverrides) key, which JS always coerces to
    // a string - billing_descriptor_id on the fetched descriptors is a number, so a strict
    // === would never match and this patch would silently no-op.
    setDescriptors((prev) =>
      prev.map((item) =>
        String(item.billing_descriptor_id) === String(id) ? { ...item, ...patch } : item
      )
    )
  }

  const clearRowOverride = (id, field) => {
    setRowOverrides((prev) => {
      const { [id]: current, ...rest } = prev
      if (!current) return prev
      const { [field]: _removed, ...remainingFields } = current
      return Object.keys(remainingFields).length ? { ...rest, [id]: remainingFields } : rest
    })
  }

  // Picking a new bulk value from the toolbar is a deliberate "apply this to everything I've
  // checked" signal - it should win over a per-row edit made earlier for one of those same
  // checked rows, otherwise that row keeps showing its old pick and silently ignores the new
  // bulk value even though it's still selected.
  const handleSetBulkStatus = (key) => {
    setBulkStatus(key)
    selectedIds.forEach((id) => clearRowOverride(id, "status"))
  }

  const handleSetBulkMatch = (key) => {
    setBulkMatch(key)
    selectedIds.forEach((id) => clearRowOverride(id, "descriptor_type"))
  }

  const handleApply = () => {
    if (!applyEnabled) return

    const tasks = []

    if (selectedIds.length > 0 && (bulkStatus || bulkMatch)) {
      tasks.push(() =>
        dispatch(
          bulkUpdateAdminDescriptorStatus({
            descriptor_ids: selectedIds,
            enrollment: enrollmentKey,
            enrollment_id: enrollment?.id,
            ...(bulkStatus ? { status: bulkStatus } : {}),
            ...(bulkMatch ? { match_type: bulkMatch } : {}),
            send_email: sendEmail
          })
        ).unwrap()
      )
    }

    Object.entries(rowOverrides).forEach(([id, override]) => {
      if (override.status) {
        tasks.push(() =>
          dispatch(
            updateAdminDescriptorStatus({
              id,
              status: override.status,
              enrollment_name: enrollmentKey
            })
          )
            .unwrap()
            .then(() => {
              patchDescriptor(id, { current_status: override.status })
              clearRowOverride(id, "status")
            })
        )
      }
      if (override.descriptor_type) {
        tasks.push(() =>
          dispatch(
            updateAdminDescriptorType({
              id,
              descriptor_type: override.descriptor_type
            })
          )
            .unwrap()
            .then(() => {
              patchDescriptor(id, { descriptor_type: override.descriptor_type })
              clearRowOverride(id, "descriptor_type")
            })
        )
      }
    })

    if (!tasks.length) return

    setApplying(true)

    // Run one at a time, not all at once: several row overrides can target the same
    // descriptor (a status change and a type change), and applying them out of order
    // against local state (patchDescriptor/clearRowOverride) would race.
    const runTask = async (task) => {
      try {
        await task()
        return true
      } catch {
        return false
      }
    }

    ;(async () => {
      let hadError = false
      for (const task of tasks) {
        const ok = await runTask(task)
        if (!ok) hadError = true
      }

      setApplying(false)
      if (hadError) {
        notification.error({
          message: t("Descriptor status was not updated"),
          description: t("Some changes could not be saved. Please try again."),
          closeIcon: <Text>{t("Dismiss")}</Text>
        })
      } else {
        notification.success({
          message: t("Descriptors updated successfully!"),
          closeIcon: <Text>{t("Dismiss")}</Text>
        })
        onClose()
      }
    })()
  }

  const statusMenuItems = STATUS_OPTIONS.map((item) => ({
    key: item.key,
    label: (
      <Flex align="center" gap={12}>
        <span className={classname("bulk-edit-dot", `bulk-edit-dot-${getStatusType(item.key)}`)} />
        <span>{t(item.label)}</span>
      </Flex>
    )
  }))

  const matchMenuItems = MATCH_OPTIONS.map((item) => ({
    key: item.key,
    label: t(item.label)
  }))

  const handleRowStatusChange = (descriptor, option) => {
    if (!descriptor?.billing_descriptor_id || !option?.key) return
    const id = descriptor.billing_descriptor_id

    setRowOverrides((prev) => ({
      ...prev,
      [id]: { ...prev[id], status: option.key }
    }))
  }

  const handleRowTypeChange = (descriptor, option) => {
    if (!descriptor?.billing_descriptor_id || !option?.key) return
    const id = descriptor.billing_descriptor_id

    setRowOverrides((prev) => ({
      ...prev,
      [id]: { ...prev[id], descriptor_type: option.key }
    }))
  }

  return (
    <div className="bulk-edit-modal">
      <div className="bulk-edit-header">
        <Text className="bulk-edit-title">{t("Bulk edit billing descriptors")}</Text>
        <Text type="secondary" className="bulk-edit-subtitle">
          {t(
            "Select descriptors to update their status or enrollment. Bulk changes apply to all selected descriptors."
          )}
        </Text>
      </div>

      <Flex align="center" justify="space-between" gap={12} className="bulk-edit-controls">
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="bulk-edit-select-all"
        >
          {t("Select all")}
        </Checkbox>

        <Flex align="center" gap={12}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: statusMenuItems,
              onClick: ({ key }) => handleSetBulkStatus(key)
            }}
            overlayClassName="bulk-edit-dropdown"
          >
            <Flex align="center" justify="space-between" gap={8} className="bulk-edit-trigger">
              {bulkStatus ? (
                <Flex align="center" gap={8}>
                  <span
                    className={classname(
                      "bulk-edit-dot",
                      `bulk-edit-dot-${getStatusType(bulkStatus)}`
                    )}
                  />
                  <Text strong>{t(getStatusLabel(bulkStatus))}</Text>
                </Flex>
              ) : (
                <Text type="secondary" strong>
                  {t("Set status")}
                </Text>
              )}
              <Icon type="chevron_down_small" className="bulk-edit-arrow" />
            </Flex>
          </Dropdown>

          <Dropdown
            trigger={["click"]}
            menu={{
              items: matchMenuItems,
              onClick: ({ key }) => handleSetBulkMatch(key)
            }}
            overlayClassName="bulk-edit-dropdown"
          >
            <Flex align="center" justify="space-between" gap={8} className="bulk-edit-trigger">
              <Text type="secondary" strong>
                {bulkMatch ? t(bulkMatch) : t("Set match")}
              </Text>
              <Icon type="chevron_down_small" className="bulk-edit-arrow" />
            </Flex>
          </Dropdown>
        </Flex>
      </Flex>

      <div className="bulk-edit-list">
        {loading ? (
          <Flex justify="center" align="center" style={{ minHeight: 200 }}>
            <Spin size="large" />
          </Flex>
        ) : descriptors.length === 0 ? (
          <Flex justify="center" align="center" style={{ minHeight: 160 }}>
            <Text type="secondary">{t("No descriptors found.")}</Text>
          </Flex>
        ) : (
          descriptors.map((descriptor) => {
            const id = descriptor.billing_descriptor_id
            const override = rowOverrides[id]
            const isSelected = selectedSet.has(id)
            // Precedence: a row's own edit wins, then the toolbar's pending bulk value while
            // the row is checked (previewing what Apply will do to it), then its saved value.
            const previewStatus = override?.status ?? (isSelected ? bulkStatus : null)
            const previewMatch = override?.descriptor_type ?? (isSelected ? bulkMatch : null)

            return (
              <Flex
                key={id}
                align="center"
                justify="space-between"
                gap={16}
                className="bulk-edit-row"
              >
                <Checkbox
                  checked={selectedSet.has(id)}
                  onChange={() => handleToggleRow(id)}
                  className="bulk-edit-row-check"
                />
                <Flex align="center" gap={12} className="bulk-edit-row-main">
                  <Text strong className="bulk-edit-row-name" ellipsis>
                    {descriptor.billing_descriptor}
                  </Text>

                  <span className="bulk-edit-row-divider" />

                  <Flex align="center" gap={8} className="bulk-edit-row-ba">
                    <Avatar size={20} className="bulk-edit-avatar">
                      {(businessAccountName || "?").slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Text type="secondary" ellipsis>
                      {businessAccountName}
                    </Text>
                  </Flex>

                  <span className="bulk-edit-row-divider" />

                  <Flex align="center" gap={8} className="bulk-edit-row-enrollment">
                    <Icon type={enrollmentIcon} className="bulk-edit-enrollment-icon" />
                    <Text type="secondary">{enrollmentLabel}</Text>
                  </Flex>
                </Flex>

                <DescriptorHeaderAddon
                  status={previewStatus ?? descriptor.current_status}
                  descriptorType={previewMatch ?? descriptor.descriptor_type}
                  onStatusChange={(option) => handleRowStatusChange(descriptor, option)}
                  onDescriptorTypeChange={(option) => handleRowTypeChange(descriptor, option)}
                  statusFirst
                />
              </Flex>
            )
          })
        )}
      </div>

      <Flex align="center" className="bulk-edit-email">
        <Switch checked={sendEmail} onChange={setSendEmail} />
        <Text className="bulk-edit-email-label">{t("Send an email about the status change")}</Text>
      </Flex>

      <Flex align="center" justify="space-between" gap={12} className="bulk-edit-footer">
        <Text type="secondary" className="bulk-edit-counter">
          <Text strong>{selectedIds.length}</Text> {t("descriptors selected")}
        </Text>

        <Flex align="center" gap={12}>
          <ButtonComponent size="large" onClick={onClose} className="bulk-edit-cancel">
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            size="large"
            type="primary"
            disabled={!applyEnabled}
            loading={applying}
            onClick={handleApply}
          >
            {t("Apply")}
          </ButtonComponent>
        </Flex>
      </Flex>
    </div>
  )
}

const BulkEditModal = ({ open, onClose, enrollment, businessAccountName }) => {
  const { t } = useTranslation()

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={920}
      centered
      closeIcon={<Icon type="close_modal" />}
      title={null}
      className="bulk-edit-modal-root"
      classNames={{ content: "clear-bg" }}
      aria-label={t("Bulk edit billing descriptors")}
    >
      {open && (
        <BulkEditModalBody
          enrollment={enrollment}
          businessAccountName={businessAccountName}
          onClose={onClose}
        />
      )}
    </Modal>
  )
}

export default BulkEditModal
